import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getEmailSend, listEmailEvents, resendEmail } from "@/lib/api";
import { fmtDateTime, fmtRelative, emailStatusLabel, emailStatusClass, emailEventLabel } from "@/lib/formatters";
import { useState } from "react";
import type { Json } from "@/types/database";

function payloadObject(payload: Json): Record<string, unknown> {
  return payload && typeof payload === "object" && !Array.isArray(payload)
    ? payload as Record<string, unknown>
    : {};
}

function nestedString(value: unknown, key: string): string | null {
  return value && typeof value === "object" && key in value
    ? String((value as Record<string, unknown>)[key] ?? "")
    : null;
}

function eventDetail(payload: Json): string {
  const p = payloadObject(payload);
  const bounce = p.bounce;
  const complaint = p.complaint;
  const error = p.error;
  const reason = p.reason ?? p.message;

  return [
    nestedString(bounce, "message"),
    nestedString(bounce, "type"),
    nestedString(bounce, "subType"),
    nestedString(complaint, "type"),
    typeof error === "string" ? error : nestedString(error, "message"),
    typeof reason === "string" ? reason : null,
  ].filter(Boolean).join(" · ");
}

export function EmailDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);

  const { data: send, isLoading } = useQuery({
    queryKey: ["email-send", id],
    queryFn: () => getEmailSend(id!),
    enabled: !!id,
  });

  const { data: events } = useQuery({
    queryKey: ["email-events", id],
    queryFn: () => listEmailEvents(id!),
    enabled: !!id,
  });

  const resendMut = useMutation({
    mutationFn: () => resendEmail(id!),
    onSuccess: () => {
      setToast("Mail opnieuw verzonden");
      qc.invalidateQueries({ queryKey: ["email-sends"] });
    },
    onError: (e: Error) => setToast(`Fout: ${e.message}`),
  });

  if (isLoading || !send) {
    return <div className="empty"><span className="spinner" /> Laden…</div>;
  }

  const order = send.order as unknown as { id: string; order_no: string; buyer_email: string; buyer_first_name: string | null; buyer_last_name: string | null; total_cents: number; status: string; flow: string } | null;
  const campaign = send.campaign as unknown as { id: string; name: string } | null;

  return (
    <>
      <div style={{ marginBottom: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
        <Link to="/emails" className="btn-mini">← Alle mails</Link>
        <button
          className="btn-admin sm"
          disabled={resendMut.isPending}
          onClick={() => {
            if (confirm(`Deze mail opnieuw versturen naar ${send.to_email}?`)) resendMut.mutate();
          }}
        >
          ↻ Opnieuw verzenden
        </button>
      </div>

      <div className="row2">
        <div className="panel">
          <div className="panel-h"><h3>Mail</h3></div>
          <dl className="dl">
            <dt>Onderwerp</dt><dd>{send.subject ?? <span style={{ opacity: 0.5 }}>—</span>}</dd>
            <dt>Naar</dt><dd>{send.to_email}</dd>
            <dt>Van</dt><dd>{send.from_email ?? <span style={{ opacity: 0.5 }}>—</span>}</dd>
            <dt>Template</dt><dd>{send.template_slug ? <code>{send.template_slug}</code> : "—"}</dd>
            <dt>Status</dt><dd><span className={`pill-st ${emailStatusClass(send.status)}`}>{emailStatusLabel(send.status)}</span></dd>
            <dt>Resend ID</dt><dd><code style={{ fontSize: "0.75rem", opacity: 0.7 }}>{send.resend_id ?? "—"}</code></dd>
            {send.error && <><dt>Fout</dt><dd style={{ color: "var(--c-red, #c33)" }}>{send.error}</dd></>}
          </dl>
        </div>

        <div className="panel">
          <div className="panel-h"><h3>Tijdlijn</h3></div>
          <dl className="dl">
            <dt>Aangemaakt</dt><dd>{fmtDateTime(send.created_at)}</dd>
            {send.sent_at && <><dt>Verzonden</dt><dd>{fmtDateTime(send.sent_at)}</dd></>}
            {send.delivered_at && <><dt>Afgeleverd</dt><dd>{fmtDateTime(send.delivered_at)}</dd></>}
            {send.opened_at && <><dt>Eerste open</dt><dd>{fmtDateTime(send.opened_at)} <small>({send.open_count}× totaal)</small></dd></>}
            {send.clicked_at && <><dt>Eerste klik</dt><dd>{fmtDateTime(send.clicked_at)} <small>({send.click_count}× totaal)</small></dd></>}
            {send.bounced_at && <><dt>Bounced</dt><dd>{fmtDateTime(send.bounced_at)}</dd></>}
            {send.complained_at && <><dt>Klacht</dt><dd>{fmtDateTime(send.complained_at)}</dd></>}
          </dl>
        </div>
      </div>

      {(order || campaign) && (
        <div className="row2">
          {order && (
            <div className="panel">
              <div className="panel-h"><h3>Bestelling</h3></div>
              <dl className="dl">
                <dt>Bestelnr</dt><dd><Link to={`/orders/${order.id}`}><code>{order.order_no}</code></Link></dd>
                <dt>Klant</dt><dd>{order.buyer_first_name} {order.buyer_last_name}</dd>
                <dt>E-mail</dt><dd>{order.buyer_email}</dd>
              </dl>
            </div>
          )}
          {campaign && (
            <div className="panel">
              <div className="panel-h"><h3>Campagne</h3></div>
              <dl className="dl">
                <dt>Naam</dt><dd>{campaign.name}</dd>
              </dl>
            </div>
          )}
        </div>
      )}

      <div className="panel" style={{ marginTop: "1rem" }}>
        <div className="panel-h"><h3>Event log ({events?.length ?? 0})</h3></div>
        {(events ?? []).length === 0 ? (
          <div className="empty">Nog geen webhook events ontvangen.</div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Wanneer</th>
                  <th>Type</th>
                  <th>Detail</th>
                  <th>IP / UA</th>
                </tr>
              </thead>
              <tbody>
                {events!.map((ev) => (
                  <tr key={ev.id}>
                    <td>{fmtRelative(ev.created_at)}<br /><small style={{ opacity: 0.5 }}>{fmtDateTime(ev.created_at)}</small></td>
                    <td><strong>{emailEventLabel(ev.event_type)}</strong></td>
                    <td>
                      {ev.link && <a href={ev.link} target="_blank" rel="noreferrer" style={{ wordBreak: "break-all" }}>{ev.link}</a>}
                      {!ev.link && (eventDetail(ev.payload) || <span style={{ opacity: 0.4 }}>—</span>)}
                    </td>
                    <td>
                      {ev.ip && <small>{ev.ip}</small>}
                      {ev.user_agent && <><br /><small style={{ opacity: 0.6 }}>{ev.user_agent.slice(0, 60)}{ev.user_agent.length > 60 ? "…" : ""}</small></>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toast && <div className="toast" onClick={() => setToast(null)}>{toast}</div>}
    </>
  );
}
